import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import DatePickerInput from '@/components/atoms/DatePickerInput/DatePickerInput';
import {convertToUTC} from '../validations';
import {getStartDateSchema, getEndDateSchema} from '../schemas';
import {REWARD_STATUS} from '@/utils/constants/rewardsConstants';

/**
 * AvailabilitySection component for reward start and end dates
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing startDate and endDate
 * @param {Function} props.handleFormChange - Function to handle form field changes
 * @param {Function} props.handleValidation - Function to handle validation state
 * @returns {JSX.Element} The rendered AvailabilitySection component
 */
const AvailabilitySection = ({
  formData,
  handleFormChange,
  handleValidation
}) => {
  const handleStartDateChange = useCallback(
    (value, isValid) => {
      // Convert to UTC for API storage
      const utcValue = value ? convertToUTC(value) : null;
      handleFormChange('startDate', utcValue);
      handleFormChange('startDateDisplay', value); // Keep display format for UI

      // Update validation state
      handleValidation('startDate', !isValid);
    },
    [handleFormChange, handleValidation]
  );

  const handleEndDateChange = useCallback(
    (value, isValid) => {
      // Convert to UTC for API storage
      const utcValue = value ? convertToUTC(value) : null;
      handleFormChange('endDate', utcValue);
      handleFormChange('endDateDisplay', value); // Keep display format for UI

      // Update validation state
      handleValidation('endDate', !isValid);
    },
    [handleFormChange, handleValidation]
  );

  const isActiveReward =
    formData?.status?.toUpperCase?.() === REWARD_STATUS.ACTIVE;
  const startDateSchema = getStartDateSchema(formData, {
    isStartDateDisabled: isActiveReward,
    skipRequiredIfEndDate: isActiveReward
  });
  const endDateSchema = getEndDateSchema(formData);

  return (
    <div className='flex flex-col gap-6'>
      <h3 className='text-base font-bold text-white'>Availability</h3>

      <div className='flex flex-col items-start gap-6 md:flex-row'>
        <DatePickerInput
          value={formData?.startDateDisplay || ''}
          updateValue={handleStartDateChange}
          uiSchema={startDateSchema}
          id='start_date'
        />
        <DatePickerInput
          value={formData?.endDateDisplay || ''}
          updateValue={handleEndDateChange}
          uiSchema={endDateSchema}
          id='end_date'
        />
      </div>
    </div>
  );
};

export default React.memo(AvailabilitySection);

AvailabilitySection.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  handleValidation: PropTypes.func.isRequired
};
